import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Activity, FileText, PenTool, Brain, Tag, Calendar, StickyNote, Upload, RotateCcw, Loader2 } from 'lucide-react';

const ACTION_ICONS = {
  signed: { icon: PenTool, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  analyzed: { icon: Brain, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  imported: { icon: Upload, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  deadlines_updated: { icon: Calendar, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  notes_updated: { icon: StickyNote, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  tags_updated: { icon: Tag, color: 'text-pink-400', bg: 'bg-pink-500/10' },
  version_created: { icon: RotateCcw, color: 'text-slate-400', bg: 'bg-slate-500/10' },
};

const DEFAULT_ICON = { icon: FileText, color: 'text-slate-400', bg: 'bg-slate-500/10' };

const ActivityLog = ({ contractId }) => {
  const { api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivity();
  }, [contractId]);

  const fetchActivity = async () => {
    try {
      const response = await api.get(`/contracts/${contractId}/activity`);
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      const mins = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
      if (mins < 1) return 'hace un momento';
      if (mins < 60) return `hace ${mins}m`;
      if (hours < 24) return `hace ${hours}h`;
      if (days < 7) return `hace ${days}d`;
      return date.toLocaleDateString('es-CL');
    } catch {
      return timestamp;
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-700 rounded w-1/4"></div>
          {[1,2,3].map(i => (
            <div key={i} className="flex gap-3">
              <div className="w-8 h-8 bg-slate-700 rounded-lg flex-shrink-0"></div>
              <div className="flex-1 space-y-1">
                <div className="h-3 bg-slate-700 rounded w-3/4"></div>
                <div className="h-2 bg-slate-700/50 rounded w-1/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="text-sm font-semibold flex items-center gap-2 mb-4">
        <Activity className="w-4 h-4 text-slate-400" />
        Historial de Actividad
      </h3>

      {activities.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">Sin actividad registrada aún</p>
      ) : (
        <div className="space-y-3">
          {activities.map((act, idx) => {
            const { icon: Icon, color, bg } = ACTION_ICONS[act.action] || DEFAULT_ICON;
            return (
              <div key={idx} className="flex items-start gap-3">
                <div className={`w-8 h-8 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4 h-4 ${color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-300 truncate">{act.detail}</p>
                  <p className="text-xs text-slate-500">{formatTime(act.timestamp)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
